/*
 * @copyright Copyright (c) valantic CEC Deutschland GmbH (https://www.valantic.com)
 * @see PROJECT_LICENSE.txt
 */

import {
    getImagesFromAttribute,
    getImageLabel,
    getAttributeValue,
    getFakeSwatches,
    getRating,
    overlayerAttributeIsSet,
    mapGtmItem
} from '../../api/mapping'
import { selectOverlayBadges } from './overlayBadges'

const liveSearchProductsMapping = {
    /**
     * Build a stable render fingerprint for a product card.
     *
     * @param {Object} item
     * @returns {string}
     */
    getProductRenderFingerprint(item) {
        const productId = item?.product?.id ?? ''
        const productSku = item?.product?.sku ?? ''
        const canonicalUrl = item?.product?.canonical_url ?? ''
        const masterNumber = getAttributeValue(item, 'master_number') ?? ''

        return [productId, canonicalUrl, productSku, masterNumber]
            .map((value) => String(value).trim())
            .join('|')
    },
    /**
     * Build a stable instance key for a rendered product card.
     *
     * @param {Object} item
     * @param {number} pagination
     * @param {number} index
     * @returns {string}
     */
    getProductInstanceKey(item, pagination, index) {
        const renderFingerprint = this.getProductRenderFingerprint(item)

        return `${renderFingerprint}|page-${pagination}|slot-${index + 1}`
    },
    /**
     * Build a DOM-safe id for a rendered product card.
     *
     * @param {string} instanceKey
     * @returns {string}
     */
    getProductDomId(instanceKey) {
        const safeInstanceKey = String(instanceKey)
            .trim()
            .replace(/[^a-zA-Z0-9_-]+/g, '-')
            .replace(/^-+|-+$/g, '')

        return `product-item-${safeInstanceKey || 'unknown'}`
    },
    /**
     * Compute the sale percentage badge value the same way the backend does.
     *
     * @param {Object} priceRange
     * @returns {number}
     */
    getSalePercentage(priceRange) {
        const maxPrice = priceRange?.maximum_price?.regular_price?.value ?? 0
        const finalPrice = priceRange?.maximum_price?.final_price?.value ?? 0

        return maxPrice > 0 ? Math.round(((maxPrice - finalPrice) / maxPrice) * 100) : 0
    },
    /**
     * Compute the overlay badges to show for a product, already selected and ordered (MEY3-2280).
     *
     * @param {Object} item
     * @param {number} salePercentage
     * @returns {string[]}
     */
    getOverlayBadges(item, salePercentage) {
        const activeBadgeKeys = [
            overlayerAttributeIsSet(item, 'overlayer_sale') && salePercentage > 0 ? 'sale' : null,
            overlayerAttributeIsSet(item, 'overlayer_new') ? 'new' : null,
            overlayerAttributeIsSet(item, 'overlayer_forwinter') ? 'forwinter' : null,
            overlayerAttributeIsSet(item, 'overlayer_forallseasons') ? 'allseasons' : null,
            getAttributeValue(item, 'mey_innovation') ? 'innovative' : null,
            getAttributeValue(item, 'sustainability') ? 'sustainable' : null
        ].filter(Boolean)

        return selectOverlayBadges(activeBadgeKeys)
    },
    /**
     * Merge products
     *
     * @param products
     * @param type
     * @returns {*}
     */
    mergeProducts(products, type) {
        const mergedProducts = type === 'next'
            ? [...this.products, ...products]
            : [...products, ...this.products]
        return this.products = mergedProducts
    },
    /**
     * Map products
     *
     * @param items
     * @param pagination
     * @returns {*[]}
     */
    mapProducts(items, pagination) {
        const loadedProducts = this.products.length

        return items.map((item, index) => {
            const renderFingerprint = this.getProductRenderFingerprint(item)
            const instanceKey = this.getProductInstanceKey(item, pagination, index)
            const priceRange = item.product.price_range
            const salePercentage = this.getSalePercentage(priceRange)

            return {
                instanceKey,
                domId: this.getProductDomId(instanceKey),
                renderFingerprint,
                renderKey: instanceKey,
                count: loadedProducts + index + 1,
                isFirstOfPage: index === 0,
                page: pagination,
                name: item.product.name,
                priceRange,
                currency: priceRange?.maximum_price?.regular_price?.currency ?? '',
                images: getImagesFromAttribute(item),
                imageLabel: getImageLabel(item),
                url: item.product.canonical_url,
                id: item.product.id,
                sku: item.product.sku,
                masterNumber: getAttributeValue(item, 'master_number'),
                salePercentage,
                overlayBadges: this.getOverlayBadges(item, salePercentage),
                style: getAttributeValue(item, 'style'),
                designColor: getAttributeValue(item, 'design_color_select'),
                color: getAttributeValue(item, 'color'),
                fakeSwatches: getFakeSwatches(item),
                rating: getRating(item),
                gtmItem: mapGtmItem(item)
            }
        })
    }
}

export default liveSearchProductsMapping
